import {
  DiagnosticResult,
  DiagnosticDetails,
  DiagnosticFinding,
  FillStatus,
  ImpressionSummaryRow,
  Step5Data,
  Step6Data
} from '../../models/diagnostic.models';

export interface StepEntry {
  key: string;
  data: any;
  exitedHere: boolean;
  defaultOpen: boolean;
}

export interface EarlyExitCardState {
  isCompletion: boolean;
  stepEntries: StepEntry[];
  openSteps: Record<string, boolean>;
  otherEntries: [string, any][];
  findings: DiagnosticFinding[];
}

const KEY_LABELS: Record<string, string> = {
  campaign_status: 'Status', start_date: 'Inicio', end_date: 'Fin',
  budget: 'Budget', spend_data: 'Spend (data)', spend_ad_server: 'Spend (ad server)',
  remaining_budget: 'Remaining', ads_written: 'Ads escritos hoy', total_rows: 'Filas (defrag)',
  total_combinations: 'Total combinaciones', total_categories: 'Total categorías',
  total_products: 'Total productos', products_with_stock: 'Productos con stock',
  products_without_stock: 'Productos sin stock', categorie_id: 'Categoría ID',
  category_name: 'Nombre', keywords: 'Keywords', product_id: 'Product ID',
  in_stock_stores: 'Tiendas con stock', total_stores_with_stock: 'Tiendas con stock',
  stock_rank: '#', store_rank: '#', store_id: 'Store ID', super_store_id: 'Super Store ID',
  store_type: 'Store type', store_name: 'Tienda', store_address: 'Dirección',
  stock: 'Stock', products_in_store: 'Productos en tienda', campaign_id: 'Campaign ID',
  placement: 'Placement', ad_slot: 'Ad slot', renders: 'Renders',
  renders_planned: 'Planificados', renders_delivered: 'Entregados',
  fill_rate: 'Fill rate', fill_status: 'Estado', ad_slots: 'Ad slots',
  total_pairs: 'Total combinaciones', total_renders_planned: 'Total planificados',
  total_renders_delivered: 'Total entregados', overall_fill_rate: 'Fill rate global'
};

const MONEY_KEYS = new Set(['budget', 'spend_data', 'spend_ad_server', 'remaining_budget']);

export function safe(val: any, fallback = '—'): any {
  return val !== null && val !== undefined ? val : fallback;
}

export function labelFor(key: string): string {
  return KEY_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function formatMoney(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
}

export function formatNumber(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('es-CO').format(value);
}

export function formatFillRate(value: number | null): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return `${(Math.min(100, Math.max(0, value * 100))).toFixed(1)}%`;
}

export function formatValue(key: string, value: any): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }
  if (MONEY_KEYS.has(key) && typeof value === 'number') {
    return formatMoney(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '—' : value.join(', ');
  }
  return String(value);
}

export function isPrimitive(value: any): boolean {
  return !Array.isArray(value) && !(value && typeof value === 'object');
}

export function isArrayOfObjects(value: any): boolean {
  return Array.isArray(value) && value.length > 0 && value.every(it => it && typeof it === 'object' && !Array.isArray(it));
}

export function isPlainObject(value: any): boolean {
  return value && typeof value === 'object' && !Array.isArray(value);
}

export function primitiveEntries(data: any): [string, any][] {
  return Object.entries(data || {}).filter(([, v]) => isPrimitive(v));
}

export function tableEntries(data: any): [string, any[]][] {
  return Object.entries(data || {}).filter(([, v]) => isArrayOfObjects(v)) as [string, any[]][];
}

export function objectEntries(data: any): [string, any][] {
  return Object.entries(data || {}).filter(([, v]) => isPlainObject(v) && !isArrayOfObjects(v));
}

export function tableColumns(rows: any[]): string[] {
  const cols = new Set<string>();
  rows.forEach(r => Object.keys(r).forEach(k => cols.add(k)));
  return Array.from(cols);
}

export function step1BudgetPct(data: any): number {
  const budget = data?.budget || 0;
  const spend = Math.max(data?.spend_data || 0, data?.spend_ad_server || 0);
  return budget > 0 ? Math.min(100, (spend / budget) * 100) : 0;
}

export function step1BarColor(pct: number): string {
  if (pct >= 100) {
    return '#f43f5e';
  }
  if (pct >= 80) {
    return '#f59e0b';
  }
  return '#10b981';
}

export function step0StatusTone(data: any): string {
  const s = (data?.campaign_status || '').toLowerCase();
  if (s === 'active') {
    return 'emerald';
  }
  if (s === 'paused') {
    return 'amber';
  }
  return 'rose';
}

export function fillRateBarColor(value: number | null): string {
  if (value === null || value === undefined) {
    return '#94a3b8';
  }
  if (value >= 1) {
    return '#f59e0b';
  }
  if (value === 0) {
    return '#f43f5e';
  }
  return '#10b981';
}

export function fillRatePct(value: number | null): number {
  if (value === null || value === undefined) {
    return 0;
  }
  return Math.min(100, Math.max(0, value * 100));
}

export function fillStatusClass(status: FillStatus): string {
  const map: Record<FillStatus, string> = {
    delivering: 'eec-badge--emerald',
    exhausted: 'eec-badge--amber',
    no_delivery: 'eec-badge--rose',
    no_data: 'eec-badge--slate'
  };
  return map[status] || 'eec-badge--slate';
}

export function fillStatusDot(status: FillStatus): string {
  const map: Record<FillStatus, string> = {
    delivering: '#10b981',
    exhausted: '#f59e0b',
    no_delivery: '#f43f5e',
    no_data: '#94a3b8'
  };
  return map[status] || '#94a3b8';
}

export function step6OverallStatus(data: Step6Data): FillStatus {
  const fr = data?.overall_fill_rate;
  if (fr === null || fr === undefined) {
    return 'no_data';
  }
  if (fr >= 1) {
    return 'exhausted';
  }
  if (fr === 0) {
    return 'no_delivery';
  }
  return 'delivering';
}

export function step6Headline(data: Step6Data): string {
  const s = step6OverallStatus(data);
  if (s === 'no_delivery') {
    return 'Sin impresiones entregadas';
  }
  if (s === 'exhausted') {
    return 'Inventario completado';
  }
  if (s === 'no_data') {
    return 'Sin datos disponibles';
  }
  return 'Entrega en curso';
}

export function step6SortedSummary(data: Step6Data): ImpressionSummaryRow[] {
  const statusOrder: Record<FillStatus, number> = { no_delivery: 0, no_data: 1, exhausted: 2, delivering: 3 };
  return [...(data?.summary || [])].sort((a, b) => {
    const oa = statusOrder[a.fill_status] ?? 9;
    const ob = statusOrder[b.fill_status] ?? 9;
    return oa !== ob ? oa - ob : (a.fill_rate ?? 0) - (b.fill_rate ?? 0);
  });
}

export function step6NoDeliveryCount(data: Step6Data): number {
  return (data?.summary || []).filter(s => s.fill_status === 'no_delivery').length;
}

export function step5HasMatch(data: Step5Data): boolean {
  const target = data?.url_store_id;
  if (!target) {
    return false;
  }
  return (data?.rows || []).some(r => String(r.store_id) === String(target));
}

export function donutRadius(size: number): number {
  return (size - 14) / 2;
}

export function donutCircumference(size: number): number {
  return 2 * Math.PI * donutRadius(size);
}

export function donutOffset(value: number | null, size: number): number {
  const c = donutCircumference(size);
  const pct = value === null || value === undefined ? 0 : Math.min(100, Math.max(0, value * 100));
  return c - (pct / 100) * c;
}

export function donutPath(value: number | null, size: number): string {
  const radius = (size - 14) / 2;
  const c = 2 * Math.PI * radius;
  const pct = value === null || value === undefined ? 0 : Math.min(100, Math.max(0, value * 100));
  const offset = c - (pct / 100) * c;
  return `${c} ${offset}`;
}

export function donutColor(value: number | null): string {
  return fillRateBarColor(value);
}

export function donutDisplay(value: number | null): string {
  return formatFillRate(value);
}

export function severity(findings: DiagnosticFinding[]): 'ok' | 'warning' | 'critical' {
  let criticals = 0;
  let warnings = 0;
  for (const f of findings) {
    if (f.severity === 'critical') {
      criticals++;
    } else if (f.severity === 'warning') {
      warnings++;
    }
  }
  if (criticals > 0) {
    return 'critical';
  }
  if (warnings > 0) {
    return 'warning';
  }
  return 'ok';
}

export function completionHeadline(findings: DiagnosticFinding[]): string {
  let criticals = 0;
  let warnings = 0;
  for (const f of findings) {
    if (f.severity === 'critical') {
      criticals++;
    } else if (f.severity === 'warning') {
      warnings++;
    }
  }
  if (criticals > 0) {
    return `${criticals} problema${criticals === 1 ? '' : 's'} crítico${criticals === 1 ? '' : 's'} detectado${criticals === 1 ? '' : 's'}.`;
  }
  if (warnings > 0) {
    return `${warnings} observación${warnings === 1 ? '' : 'es'} en el diagnóstico.`;
  }
  return 'La campaña pasa todos los chequeos del diagnóstico.';
}

export function findingsByType(findings: DiagnosticFinding[], sev: string): DiagnosticFinding[] {
  return findings.filter(f => f.severity === sev);
}

export function findingDotColor(sev: string): string {
  if (sev === 'ok') {
    return '#10b981';
  }
  if (sev === 'warning') {
    return '#f59e0b';
  }
  return '#f43f5e';
}

export function stepConfigFor(key: string): { title: string; subtitle: string; accent: string; hideStepNumber?: boolean } {
  const map: Record<string, { title: string; subtitle: string; accent: string; hideStepNumber?: boolean }> = {
    step0: { title: 'Estado', subtitle: 'Status de la campaña', accent: 'indigo' },
    step1: { title: 'Budget', subtitle: 'Presupuesto y gasto', accent: 'emerald' },
    step2: { title: 'Ads escritos', subtitle: 'Verificación en defrag', accent: 'sky' },
    step3: { title: 'Combinaciones', subtitle: 'Placement × Store type × Categoría', accent: 'violet' },
    step4: { title: 'Keywords', subtitle: 'Palabras del cloud por categoría', accent: 'amber' },
    step5: { title: 'Stock', subtitle: 'Disponibilidad por tienda', accent: 'rose' },
    step6: { title: 'Impresiones', subtitle: 'Renders planificados vs entregados hoy', accent: 'teal', hideStepNumber: true },
    step7: { title: 'Sugerencias · Dónde sí está entregando', subtitle: '', accent: 'violet', hideStepNumber: true }
  };
  return map[key] || { title: key, subtitle: '', accent: 'slate' };
}

export function stepAccentClass(accent: string): string {
  const map: Record<string, string> = {
    indigo: 'eec-accent--indigo', emerald: 'eec-accent--emerald', sky: 'eec-accent--sky',
    violet: 'eec-accent--violet', amber: 'eec-accent--amber', rose: 'eec-accent--rose',
    teal: 'eec-accent--teal', slate: 'eec-accent--slate'
  };
  return map[accent] || 'eec-accent--slate';
}

export function stepNumber(key: string): number {
  return Number(key.replace('step', ''));
}

export function toggleInMap(map: Record<string, boolean>, key: string): void {
  map[key] = !map[key];
}

function extractRootSteps(data: any): DiagnosticDetails {
  const result: DiagnosticDetails = {};
  Object.keys(data || {}).forEach(k => {
    if (/^step\d+$/.test(k)) {
      result[k] = data[k];
    }
  });
  return result;
}

function buildStep0Finding(step0: any): DiagnosticFinding | null {
  const status = (step0.campaign_status || '').toLowerCase();
  if (status === 'active') {
    return { severity: 'ok', text: `Campaña activa (vigente del ${step0.start_date} al ${step0.end_date}).` };
  }
  return null;
}

function buildStep1Finding(step1: any): DiagnosticFinding | null {
  const budget = step1.budget;
  const remaining = step1.remaining_budget;
  if (typeof budget !== 'number' || typeof remaining !== 'number') {
    return null;
  }
  const pctLeft = budget > 0 ? (remaining / budget) * 100 : 0;
  if (remaining <= 0) {
    return { severity: 'critical', text: 'Budget agotado — la campaña no tiene presupuesto restante.' };
  }
  if (pctLeft < 10) {
    return { severity: 'warning', text: `Budget casi agotado: ${formatMoney(remaining)} restante (${pctLeft.toFixed(1)}% del total).` };
  }
  return { severity: 'ok', text: `Budget disponible: ${formatMoney(remaining)} restante de ${formatMoney(budget)}.` };
}

function buildStep2Finding(step2: any): DiagnosticFinding | null {
  if (step2.ads_written) {
    return { severity: 'ok', text: `${formatNumber(step2.total_rows)} filas escritas hoy en la tabla defrag.` };
  }
  if ('ads_written' in step2 && step2.ads_written === false) {
    return { severity: 'critical', text: 'No hay ads escritos hoy en la tabla defrag para esta campaña.' };
  }
  return null;
}

function buildStep3Finding(step3: any, hasStep3: boolean): DiagnosticFinding | null {
  if (!hasStep3) {
    return null;
  }
  const totalCombos = step3.total_combinations || 0;
  if (totalCombos === 0) {
    return { severity: 'critical', text: 'No hay combinaciones placement × store type configuradas para este store type.' };
  }
  return { severity: 'ok', text: `${totalCombos} combinaciones placement × ad slot × categoría configuradas.` };
}

function buildStep4Finding(step4: any, hasStep4: boolean): DiagnosticFinding | null {
  if (!hasStep4 || !step4.search_keyword) {
    return null;
  }
  const matches = step4.categories_with_match || 0;
  const totalCats = step4.total_categories || 0;
  if (matches === 0 && totalCats > 0) {
    return { severity: 'warning', text: `La keyword "${step4.search_keyword}" no aparece en las palabras del cloud de ninguna de las ${totalCats} categorías.` };
  }
  if (matches > 0) {
    return { severity: 'ok', text: `La keyword "${step4.search_keyword}" aparece en ${matches} de ${totalCats} categorías.` };
  }
  return null;
}

function buildStep5Finding(step5: any, hasStep5: boolean): DiagnosticFinding | null {
  if (!hasStep5) {
    return null;
  }
  const noStock = step5.products_without_stock || [];
  const total = step5.total_products || 0;
  if (total <= 0) {
    return null;
  }
  if (noStock.length === total) {
    return { severity: 'critical', text: `Ninguno de los ${total} productos tiene stock en este store type.` };
  }
  if (noStock.length > 0) {
    return { severity: 'warning', text: `${noStock.length} de ${total} productos no tienen stock en este store type.` };
  }
  return { severity: 'ok', text: `Los ${total} productos de la campaña tienen stock en este store type.` };
}

function buildStep6Findings(step6: any, hasStep3: boolean): DiagnosticFinding[] {
  const results: DiagnosticFinding[] = [];
  const fr = step6.overall_fill_rate;
  const planned = step6.total_renders_planned || 0;
  const delivered = step6.total_renders_delivered || 0;

  if ('overall_fill_rate' in step6) {
    if (planned === 0) {
      results.push({ severity: 'critical', text: 'No hay renders planificados hoy para esta campaña.' });
    } else if (fr === 0) {
      results.push({ severity: 'critical', text: `Fill rate global de 0% — se planificaron ${formatNumber(planned)} renders pero no se entregó ninguno.` });
    } else if (fr !== null && fr !== undefined && fr >= 1) {
      const fillText = `Fill rate ${(fr * 100).toFixed(1)}% — se entregaron ${formatNumber(delivered)} ` +
        `renders de ${formatNumber(planned)} planificados (inventario agotado).`;
      results.push({ severity: 'warning', text: fillText });
    } else if (fr !== null && fr !== undefined && fr < 0.1) {
      results.push({ severity: 'warning', text: `Fill rate global muy bajo: ${(fr * 100).toFixed(1)}% (${formatNumber(delivered)} de ${formatNumber(planned)} renders).` });
    } else if (fr !== null && fr !== undefined) {
      results.push({ severity: 'ok', text: `Fill rate global ${(fr * 100).toFixed(1)}% — ${formatNumber(delivered)} de ${formatNumber(planned)} renders entregados.` });
    }
  }

  const summary = step6.summary || [];
  const noDelivery = summary.filter((s: any) => s.fill_status === 'no_delivery');
  if (hasStep3 && summary.length > 0 && noDelivery.length > 0) {
    results.push({
      severity: noDelivery.length === summary.length ? 'critical' : 'warning',
      text: `${noDelivery.length} de ${summary.length} combinaciones (placement × store type) no entregaron impresiones.`
    });
  }
  return results;
}

export function buildFindings(details: DiagnosticDetails): DiagnosticFinding[] {
  const findings: DiagnosticFinding[] = [];
  const step0 = details?.step0 || {} as any;
  const step1 = details?.step1 || {} as any;
  const step2 = details?.step2 || {} as any;
  const step3 = details?.step3 || {} as any;
  const step4 = details?.step4 || {} as any;
  const step5 = details?.step5 || {} as any;
  const step6 = details?.step6 || {} as any;
  const hasStep3 = 'step3' in details;
  const hasStep4 = 'step4' in details;
  const hasStep5 = 'step5' in details;

  const f0 = buildStep0Finding(step0);
  if (f0) {
    findings.push(f0);
  }
  const f1 = buildStep1Finding(step1);
  if (f1) {
    findings.push(f1);
  }
  const f2 = buildStep2Finding(step2);
  if (f2) {
    findings.push(f2);
  }
  const f3 = buildStep3Finding(step3, hasStep3);
  if (f3) {
    findings.push(f3);
  }
  const f4 = buildStep4Finding(step4, hasStep4);
  if (f4) {
    findings.push(f4);
  }
  const f5 = buildStep5Finding(step5, hasStep5);
  if (f5) {
    findings.push(f5);
  }
  findings.push(...buildStep6Findings(step6, hasStep3));

  const order: Record<string, number> = { critical: 0, warning: 1, ok: 2 };
  findings.sort((a, b) => order[a.severity] - order[b.severity]);
  return findings;
}

export function initEarlyExitCard(data: DiagnosticResult): EarlyExitCardState {
  const nested = data?.details || {};
  const hasNestedSteps = Object.keys(nested).some(k => /^step\d+$/.test(k));
  const details: DiagnosticDetails = hasNestedSteps ? nested : extractRootSteps(data);

  const isCompletion = data?.status === 'completed' ||
    (data?.exit_step ?? 0) >= 5 ||
    /completad/i.test(data?.exit_reason || '');

  const stepKeys = Object.keys(details)
    .filter(k => /^step\d+$/.test(k))
    .sort((a, b) => Number(a.replace('step', '')) - Number(b.replace('step', '')));
  const otherKeys = Object.keys(details).filter(k => !/^step\d+$/.test(k));

  const stepEntries: StepEntry[] = stepKeys.map(key => {
    const hasLargeTable = key !== 'step5' && Object.values(details[key] || {}).some(
      v => Array.isArray(v) && v.length > 10
    );
    return {
      key,
      data: details[key],
      exitedHere: !isCompletion && Number(key.replace('step', '')) === data?.exit_step,
      defaultOpen: !hasLargeTable
    };
  });

  const openSteps: Record<string, boolean> = {};
  stepEntries.forEach(e => {
    openSteps[e.key] = e.defaultOpen;
  });

  return {
    isCompletion,
    stepEntries,
    openSteps,
    otherEntries: otherKeys.map(k => [k, details[k]]),
    findings: buildFindings(details)
  };
}
